// Marker contents for the live map. These render through React portals into
// DOM nodes owned by MapLibre markers (see MapView), which is why they are
// plain components with no map knowledge at all.
//
// Two marker families, deliberately different shapes so a glance separates
// them: a live traveller is a round face, a diary moment is a dark rounded
// square. Never the same silhouette, and never separated by colour alone.
import { clusterDisplay, type Cluster } from "../lib/cluster";
import { travellersOfPlaces } from "../lib/places";
import { TravellerAvatar } from "./TravellerAvatar";
import type { LivePresence, Moment, Place, PublicUser } from "../lib/types";

export interface MapMoment {
  moment: Moment;
  /** Null for a moment whose author the server did not disclose. */
  author: PublicUser | null;
}

export interface PresenceMarkerProps {
  cluster: Cluster<LivePresence>;
  selfUserId: string | null;
  selfLabel: string;
  groupLabel: string;
  onOpen(cluster: Cluster<LivePresence>): void;
}

export function PresenceMarker({
  cluster,
  selfUserId,
  selfLabel,
  groupLabel,
  onOpen,
}: PresenceMarkerProps) {
  const members = cluster.members;
  const display = clusterDisplay(members.length);
  const faces = members.slice(0, display.visible);
  const isSelf = members.some((member) => member.item.user.id === selfUserId);
  const single = members.length === 1;
  const leader = members[0]!.item.user;

  const label = single
    ? isSelf
      ? selfLabel
      : leader.displayName
    : `${groupLabel}: ${members.length}`;

  const classes = ["live-pin"];
  if (isSelf) classes.push("live-pin-self");
  if (!single) classes.push("live-pin-group");

  return (
    <button
      type="button"
      className={classes.join(" ")}
      aria-label={label}
      title={label}
      onClick={(event) => {
        event.stopPropagation();
        onOpen(cluster);
      }}
    >
      <span className="live-stack">
        {faces.map((member, index) => (
          <span
            key={member.id}
            className="live-face"
            // The leader is the most recently seen traveller and sits in
            // front, so a stack of three still shows who arrived last.
            style={{ zIndex: faces.length - index }}
          >
            <TravellerAvatar
              user={member.item.user}
              isSelf={member.item.user.id === selfUserId}
              size="sm"
            />
          </span>
        ))}
        {display.overflow > 0 ? (
          <span className="live-more" style={{ zIndex: faces.length + 1 }}>
            +{display.overflow}
          </span>
        ) : null}
      </span>
      {/* A name under every marker would carpet the map, so only a single
          traveller gets one. A cluster is opened to see who is in it. */}
      {single ? (
        <span className="live-name">{isSelf ? selfLabel : leader.displayName}</span>
      ) : null}
    </button>
  );
}

export interface PlaceMarkerProps {
  cluster: Cluster<Place>;
  selfUserId: string | null;
  label: string;
  onOpen(cluster: Cluster<Place>): void;
}

/**
 * A DESTINATION pin, the map's primary layer: the travellers who kept a diary
 * at this place. One traveller is a single face, two or three fan out, four or
 * more show three faces plus an exact `+N`.
 */
export function PlaceMarker({ cluster, selfUserId, label, onOpen }: PlaceMarkerProps) {
  const places = cluster.members.map((member) => member.item);
  const { travellers, total } = travellersOfPlaces(places);
  const display = clusterDisplay(total);
  const faces = travellers.slice(0, display.visible);
  return (
    <button
      type="button"
      className={total > 1 ? "live-pin live-pin-group" : "live-pin"}
      aria-label={label}
      title={label}
      onClick={(event) => {
        event.stopPropagation();
        onOpen(cluster);
      }}
    >
      <span className="live-stack">
        {faces.map((traveller, index) => (
          <span key={traveller.id} className="live-face" style={{ zIndex: faces.length - index }}>
            <TravellerAvatar
              user={traveller}
              isSelf={traveller.id === selfUserId}
              size="sm"
            />
          </span>
        ))}
        {display.overflow > 0 ? (
          <span className="live-more" style={{ zIndex: faces.length + 1 }}>
            +{display.overflow}
          </span>
        ) : null}
      </span>
    </button>
  );
}
